import React, {useCallback, useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow,} from "@amzn/meridian/table"
import {PickContext} from "../pickStatus/context/PickContext";
import Pagination from "@amzn/meridian/pagination"
import Tag from "@amzn/meridian/tag";
import RouteContextMenu from "../pickStatus/RouteContextMenu";
import Link from "@amzn/meridian/link";
import {PickFilterContext} from "../pickStatus/context/PickFilterContext";
import Tooltip from "@amzn/meridian/tooltip";
import Input from "@amzn/meridian/input";
import {
    convertEpochToLocaleFormat,
    getRefreshInterval,
    isAMZLorARv2,
    isRouteAtRisk,
    nullIfDefault,
    openSCCPageInNewTab,
    sortList
} from "../util/Util";
import {FormattedMessage} from "react-intl";
import {AR_V2, CORE_AMZL} from "../util/Constants";
import {AssociateContext} from "../associate/context/AssociateContextProvider";
import {
    getPickStatusColor,
    getPickAssistStatusColor,
    getPickAssistSupportTypeColor,
    getPickAssistStatusLabel,
    getPickAssistSupportTypeLabel,
    PICK_TYPE
} from "../util/EntityStatusManager";
import * as PickColumns from "../pickStatus/PickTableColumns";
import {ROUTE_DATA_CACHE_KEY} from "../util/LocalStorageKeys";
import {getRemainingTTL, isRouteCacheValid} from "../util/CacheManager";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small"
import chevronLeftSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-left-small"
import Icon from "@amzn/meridian/icon";
import * as FilterUtil from "../util/FilterUtil"
import PicklistContextMenu from "../pickStatus/PicklistContextMenu";
import Text from "@amzn/meridian/text";
import useCapabilityContext from "../global/context/useCapabilityContext";
import TableScrollWrapper from "../commonModals/TableScrollWrapper";

const RoutePickStatus = (props) => {
    const {viewModel, dispatch} = useContext(PickContext)
    const {viewModelF, dispatchF} = useContext(PickFilterContext)
    const {configViewModel, configDispatch, configViewModelRef} = useCapabilityContext()
    const {associateModelDispatcher} = useContext(AssociateContext)

    const [expandedRoutes, setExpandedRoutes] = useState([])
    const [globalRouteExpander, setGlobalRouteExpander] = useState(false)
    const [sortColumn, setSortColumn] = useState("priority")
    const [sortDirection, setSortDirection] = useState("ascending")
    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    /** In Line Table Filters **/
    const [priorityFilter, setPriorityFilter] = useState("")
    const [routeCodeFilter, setRouteCodeFilter] = useState("")
    const [stationCodeFilter, setStationCodeFilter] = useState("")
    const [stationPairsFilter, setStationPairsFilter] = useState("")
    const [typeFilter, setTypeFilter] = useState("")
    const [statusFilter, setStatusFilter] = useState("")
    const [bagFilter, setBagFilter] = useState("")
    const [ovFilter, setOVFilter] = useState("")
    const [aisleFilter, setAisleFilter] = useState("")
    const [assignedWorkGroupFilter, setAssignedWorkGroupFilter] = useState("")
    const [nextWorkGroupsFilter, setNextWorkGroupsFilter] = useState("")
    const [sprFilter, setSPRFilter] = useState("")
    const [cartFilter, setCartFilter] = useState("")
    const [progressFilter, setProgressFilter] = useState("")
    const [slaFilter, setSLAFilter] = useState("")
    const [associateFilter, setAssociateFilter] = useState("")
    const [durationFilter, setDurationFilter] = useState("")
    const [lastPickCompletionTimeFilter, setLastPickCompletionTimeFilter] = useState("")

    /** Create clone of global route list */
    let routeList = Array.from(viewModel.routeList);

    /** Sort Routes **/
    sortList(routeList, sortColumn, sortDirection);

    const filterAisles = (route, filter) => {
        if (filter.length === 0) {
            return true;
        } else if (route.aisles.length) {
            let result = false;
            route.aisles.forEach(aisle => {
                if (aisle.startsWith(filter)) {
                    result = true;
                }
            })
            return result;
        }
        return false;
    }

    const inLineFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] != null && route[key].toString().toLowerCase().startsWith(filter.toLowerCase()));
    }

    const inLineContainsFilter = (route, filter, key) => {
        return filter.length === 0 || (route[key] != null && route[key].toString().toLowerCase().includes(filter.toLowerCase()));
    }

    const inLineAssociateFilter = (route, filterValue) => {
        if (configViewModel.capability === CORE_AMZL && configViewModel.stationConfig.parallelPickEnabled) {
            return filterValue.length === 0 || route.pickAssociatesAlias
                .filter(alias => alias.toLowerCase().includes(filterValue.toLowerCase()))
                .length > 0;
        } else {
            return inLineFilter(route, filterValue, "picker");
        }
    }

    const filterWorkGroupLabel = (workGroupList, inlineFilter) => {
        if (!inlineFilter || !inlineFilter.length) {
            return true;
        }

        if (workGroupList && workGroupList.length) {
            let result = false;
            workGroupList.forEach((wg) => {
                if (wg.startsWith(inlineFilter)) {
                    result = true;
                }
            });
            return result;
        }

        return false;
    };

    /** Apply filters on global route list */
    routeList = FilterUtil.applyGlobalPickFilters(routeList, viewModelF, configViewModel)
        .filter(route => inLineFilter(route, priorityFilter, "priority"))
        .filter(route => inLineFilter(route, routeCodeFilter, "routeCode"))
        .filter(route => inLineFilter(route, stationCodeFilter, "stationCode"))
        .filter(route => inLineContainsFilter(route, stationPairsFilter, "stationPairs"))
        .filter(route => inLineContainsFilter(route, typeFilter, "routeType"))
        .filter(route => inLineFilter(route, statusFilter, "status"))
        .filter(route => inLineFilter(route, bagFilter, "bagCount"))
        .filter(route => inLineFilter(route, ovFilter, "ovCount"))
        .filter(route => filterAisles(route, aisleFilter))
        .filter(route => filterWorkGroupLabel(route.assignedWorkGroups, assignedWorkGroupFilter))
        .filter(route => filterWorkGroupLabel(route.nextWorkGroups, nextWorkGroupsFilter))
        .filter(route => inLineFilter(route, sprFilter, "routeSPR"))
        .filter(route => inLineFilter(route, cartFilter, "cartAssigned"))
        .filter(route => inLineFilter(route, progressFilter, "pickProgress"))
        .filter(route => inLineFilter(route, slaFilter, "routeSLA"))
        .filter(route => inLineAssociateFilter(route, associateFilter))
        .filter(route => inLineFilter(route, durationFilter, "pickDuration"))
        .filter(route => inLineFilter(route, lastPickCompletionTimeFilter, "lastPickCompletionTime"))

    /** Pagination State **/
    const [currentPage, setCurrentPage] = useState(1)
    const itemOnPage = 50;
    let pageSize = Math.ceil(routeList.length / itemOnPage);
    const startPage = (currentPage  - 1) * itemOnPage;
    const endPage = (startPage + itemOnPage) > routeList.length - 1 ? routeList.length - 1 :
        (startPage + itemOnPage - 1);
    if (startPage > routeList.length) {
        setCurrentPage(1);
    }

    const onRouteClick = (route) => {
        onPicklistClicked(route, null);
    }

    const onPicklistClicked = (route, picklistId) => {
        const params = [
            "Pick",
            nullIfDefault(route.routeId),
            nullIfDefault(encodeURIComponent(route.routeStatus)),
            nullIfDefault(encodeURIComponent(route.stagingLocation)),
            nullIfDefault(route.hazmatWeight),
            nullIfDefault(route.picker),
            nullIfDefault(route.routeCode),
            nullIfDefault(route.stationCode),
            encodeURIComponent(nullIfDefault(picklistId))
        ]
        const url = "/routeDetail/" + params.join("/");
        props.history.push(url)
    }

    const expandRoute = routeId => {
        if (expandedRoutes.indexOf(routeId) !== -1) {
            setExpandedRoutes(expandedRoutes.filter(expandedRoute => expandedRoute !== routeId));
        } else {
            setExpandedRoutes(expandedRoutes.concat(routeId));
        }
    }

    const isRouteExpanded = (routeId) => {
        return (expandedRoutes.indexOf(routeId) !== -1);
    }

    const toggleGlobalExpander = () => {
        if (globalRouteExpander) {
            setExpandedRoutes([])
        } else {
            setExpandedRoutes(routeList.map(route => route.routeId))
        }
        setGlobalRouteExpander(!globalRouteExpander);
    }

    const isHighSPR = (route) => {
        return route.routeSPR >= viewModelF.sprPackageCount;
    }

    const getRouteAtRiskClassName = (route) => {
        if (isRouteAtRisk(route, viewModelF.sprPackageCount, viewModelF.sprTimeLimit)) {
            return "highlighted-error"
        } else if (isHighSPR(route)) {
            return "highlighted-warning";
        }
        return "";
    }

    const onAssociateClick = (picker) => {
        const associate = [picker]
        associateModelDispatcher({type:"SET_ASSOCIATE", data:associate})
    }

    const openAssociatePickTab = () => {
        openSCCPageInNewTab('pick', 'associatepick');
    }

    const getRouteData = () => {
        if (configViewModel.capability === AR_V2 || (configViewModel.stationConfig.initialised && Array.isArray(viewModelF.xptNodes))) {
            dispatch({
                type: 'LOAD_VIEW',
                data: {
                    dispatcher: dispatch,
                    filterDispatcher: dispatchF,
                    baseUrl: configViewModel.baseUrl,
                    capability: configViewModel.capability,
                    xptNodes: viewModelF.xptNodes,
                    stationConfig: configViewModel.stationConfig,
                    configDispatch: configDispatch
                }
            });
        }
    }

    /**
     * This gets invoked on following events:
     *  1. On every page load,
     *  2. Every time xpt filter value is changed,
     *  3. When station config's initialised attribute changes - usually happens only once for non ARv2 stations.
     *  Note: Updates on station config does not re-render useEffect, if initialised attribute value is not changed.
     */
    useEffect(() => {
        if (configViewModel.stationConfig.initialised) {
            getRouteData();
            let firstPollTime = getRefreshInterval(configViewModelRef.current.stationConfig);
            if(configViewModel.capability === CORE_AMZL) {
                const remainingTTL = getRemainingTTL(ROUTE_DATA_CACHE_KEY);
                if (viewModelF.xptNodes && isRouteCacheValid(viewModelF.xptNodes)) {
                    firstPollTime = remainingTTL;
                }
            }

            const timeoutHandler = () => {
                getRouteData();
                timeout = setTimeout(timeoutHandler, getRefreshInterval(configViewModelRef.current.stationConfig));
            }
            let timeout = setTimeout(timeoutHandler, firstPollTime);

            return () => clearTimeout(timeout);
        }
    }, [viewModelF.xptNodes, configViewModel.stationConfig.initialised]);

    const getAssociateColWithMultipleAlias = (route, onClickCallback) => {
        if (route.pickAssociatesAlias.length) {
            const responseList = [];
            for (let i = 0; i < route.pickAssociatesAlias.length && i < 2; i++) {
                responseList.push(<Row key={route.pickAssociatesAlias[i]}>
                    <Link onClick={() => {
                        if (configViewModel.capability === CORE_AMZL && configViewModel.stationConfig.parallelPickEnabled) {
                            onClickCallback(route.pickAssociatesAlias[i])
                        }
                        else if (configViewModel.capability === AR_V2) {
                            onClickCallback()
                        }
                    }} type="secondary">{route.pickAssociatesAlias[i]}</Link>
                    {i !== route.pickAssociatesAlias.length - 1 ? "," : ""}&nbsp;
                </Row>)
            }
            if (route.pickAssociatesAlias.length > 2) {
                responseList.push(<Row key={route.pickAssociatesAlias[2]}><Text>...</Text></Row>);
            }
            return <Row overflowY={"hidden"} spacing={"none"}>{responseList}</Row>;
        } else {
            return "-";
        }
    }

    const renderAssociateCol = (route) => {
        if (configViewModel.capability === CORE_AMZL && configViewModel.stationConfig.parallelPickEnabled) {
            return getAssociateColWithMultipleAlias(route, onAssociateClick);
        } else if (configViewModel.capability === CORE_AMZL) {
            if (route.picker !== "...") {
                return <Link onClick={() => {
                    onAssociateClick(route.picker)
                }} type="secondary">{route.picker}</Link>
            } else {
                return "-";
            }
        } else if (configViewModel.capability === AR_V2){
            return getAssociateColWithMultipleAlias(route, openAssociatePickTab);
        } else {
            return "-";
        }
    }

    const renderHeaderRow = () => {
        return <TableRow>
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STAGE_BY_TIME) ?
                <TableCell width={"160px"} sortColumn="routeSLA" alignmentHorizontal="right">
                    <FormattedMessage id="pick.stageByTime" defaultMessage="Stage by time"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.PICK_BY_TIME) ?
                <TableCell width={"160px"} sortColumn="routeSLA" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.pick_by_time" defaultMessage="Pick by Time"/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) && configViewModel.stationConfig.parallelPickEnabled ?
                <TableCell alignmentHorizontal="right">
                    <Link data-global-expander onClick={toggleGlobalExpander} type={"secondary"} size={"small"}>
                        {globalRouteExpander ? <Icon tokens={chevronDownSmallTokens}/> :
                            <Icon tokens={chevronLeftSmallTokens}/>}
                    </Link>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) ?
                <TableCell sortColumn="routeCode">
                    <Row spacing={"none"} wrap={"none"}>
                        <FormattedMessage id="pickStatus.routeId" defaultMessage="Route ID"/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.STATION_PAIRS) ?
                <TableCell sortColumn="stationPairs">
                    <FormattedMessage id="pickStatus.stationPairs" defaultMessage="Stations"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STATION) ?
                <TableCell sortColumn="stationCode">
                    <Row spacing={"none"} wrap={"none"}>
                        <FormattedMessage id="pickStatus.station" defaultMessage="Station"/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.PRIORITY) ?
                <TableCell sortColumn="priority" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.priority" defaultMessage="Priority"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.AISLES)?
                <TableCell sortColumn="aisles">
                    <FormattedMessage id="pick.aisles" defaultMessage="Aisles"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.ASSIGNED_WORK_GROUP) && (
                    <TableCell sortColumn="assignedWorkGroups">
                        <FormattedMessage id="pick.assignedWorkGroup" defaultMessage="Assigned Work Group"/>
                    </TableCell>
                )}
            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.NEXT_WORK_GROUPS) && (
                    <TableCell sortColumn="nextWorkGroups">
                        <FormattedMessage id="pick.nextWorkGroups" defaultMessage="Next Work Groups"/>
                    </TableCell>
                )}
            {viewModelF.selectedPickColumns.includes(PickColumns.STATUS) ?
                <TableCell sortColumn="status">
                    <FormattedMessage id="pickStatus.status" defaultMessage="Status"/>
                </TableCell>
                :null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.BAGS) ?
                <TableCell sortColumn="bagCount" alignmentHorizontal="right">
                    <FormattedMessage id="pick.bags" defaultMessage="Bags"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.OVS)?
                <TableCell sortColumn="ovCount" alignmentHorizontal="right">
                    <FormattedMessage id="pick.ovs" defaultMessage="OVs"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.CARTS) ?
                <TableCell sortColumn="cartAssigned" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.carts" defaultMessage="Carts"/>
                </TableCell>
                : null
            }
            {isAMZLorARv2(configViewModel.capability)
            && viewModelF.selectedPickColumns.includes(PickColumns.SPR) ?
                <TableCell sortColumn="routeSPR" alignmentHorizontal="right">
                    <FormattedMessage id="pick.spr" defaultMessage="SPR"/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.PROGRESS) ?
                <TableCell sortColumn="pickProgress" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.progress" defaultMessage="Progress"/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ASSOCIATE) ?
                <TableCell sortColumn="picker">
                    <FormattedMessage id="routeInfo.associate" defaultMessage="Associate"/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.DURATION) ?
                <TableCell sortColumn="pickDuration" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.duration" defaultMessage="Duration"/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.TYPE) ?
                <TableCell sortColumn="routeType">
                    <FormattedMessage id="pickStatus.type" defaultMessage="Type"/>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.LAST_PICK) ?
                <TableCell sortColumn="lastPickCompletionTime" alignmentHorizontal="right">
                    <FormattedMessage id="pickStatus.lastPick" defaultMessage="Last Pick"/>
                </TableCell>
                :null
            }
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_TYPE) && (
                    <TableCell sortColumn="pickAssistSupportTypes">
                        <FormattedMessage id="pick.assistType" defaultMessage="Assist Type"/>
                    </TableCell>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_STATUS) && (
                    <TableCell sortColumn="pickAssistStatuses">
                        <FormattedMessage id="pick.assistStatus" defaultMessage="Assist Status"/>
                    </TableCell>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_ASSOCIATE) && (
                    <TableCell sortColumn="pickAssistAssignees">
                        <FormattedMessage id="pick.assistAssociate" defaultMessage="Assist Associate"/>
                    </TableCell>
                )}
            {isAMZLorARv2(configViewModel.capability) ?
                <TableCell/>
                : null}
        </TableRow>
    }

    const renderInlineFiltersRow = () => {
        return <TableRow>
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STAGE_BY_TIME) ?
                <TableCell>
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input type="text" width={"100px"} size="small" value={slaFilter} onChange={setSLAFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.PICK_BY_TIME) ?
                <TableCell>
                    <Input type="text" size="small" value={slaFilter} onChange={setSLAFilter}/>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) && configViewModel.stationConfig.parallelPickEnabled ?
                <TableCell/>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) ?
                <TableCell>
                    <Row spacing={"none"}>
                        <Input width={"95px"} type="text" size="small" value={routeCodeFilter} onChange={setRouteCodeFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.STATION_PAIRS) ?
                <TableCell>
                    <Input type="text" size="small" value={stationPairsFilter} onChange={setStationPairsFilter} />
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STATION) ?
                <TableCell>
                    <Row spacing={"none"}>
                        <Input width={"95px"} type="text" size="small" value={stationCodeFilter} onChange={setStationCodeFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.PRIORITY) ?
                <TableCell>
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"60px"} type="text" size="small" value={priorityFilter} onChange={setPriorityFilter} />
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.AISLES)?
                <TableCell alignmentHorizontal="left">
                    <Row alignmentHorizontal="left" spacing={"none"}>
                        <Input width={"70px"} type="text" size="small" value={aisleFilter} onChange={setAisleFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.ASSIGNED_WORK_GROUP) && (
                    <TableCell alignmentHorizontal="left">
                        <Row alignmentHorizontal="left" spacing="none">
                            <Input
                                width="95px"
                                type="text"
                                size="small"
                                name="assignedWorkGroupFilter"
                                value={assignedWorkGroupFilter}
                                onChange={setAssignedWorkGroupFilter}
                            />
                        </Row>
                    </TableCell>
                )}

            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.NEXT_WORK_GROUPS) && (
                    <TableCell alignmentHorizontal="left">
                        <Row alignmentHorizontal="left" spacing="none">
                            <Input
                                width="95px"
                                type="text"
                                size="small"
                                value={nextWorkGroupsFilter}
                                name="nextWorkGroupsFilter"
                                onChange={setNextWorkGroupsFilter}
                            />
                        </Row>
                    </TableCell>
                )}

            {viewModelF.selectedPickColumns.includes(PickColumns.STATUS) ?
                <TableCell>
                    <Row alignmentHorizontal="left" spacing={"none"}>
                        <Input width={"100px"} type="text" size="small" value={statusFilter} onChange={setStatusFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.BAGS)?
                <TableCell alignmentHorizontal="right">
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"60px"} type="text" size="small" value={bagFilter} onChange={setBagFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.OVS)?
                <TableCell alignmentHorizontal="right">
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"60px"}type="text" size="small" value={ovFilter} onChange={setOVFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.SPR)?
                <TableCell alignmentHorizontal="right">
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"60px"} type="text" size="small" value={sprFilter} onChange={setSPRFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.CARTS) ?
                <TableCell alignmentHorizontal="right">
                    <Input type="text" size="small" value={cartFilter} onChange={setCartFilter}/>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.SPR)?
                <TableCell alignmentHorizontal="right">
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"60px"} type="text" size="small" value={sprFilter} onChange={setSPRFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.PROGRESS) ?
                <TableCell>
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"70px"} type="text" size="small" value={progressFilter} onChange={setProgressFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ASSOCIATE) ?
                <TableCell>
                    <Row alignmentHorizontal="left" spacing={"none"}>
                        <Input width={"95px"} type="text" size="small" value={associateFilter} onChange={setAssociateFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.DURATION) ?
                <TableCell>
                    <Row alignmentHorizontal="right" spacing={"none"}>
                        <Input width={"100px"} type="text" size="small" value={durationFilter} onChange={setDurationFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.TYPE) ?
                <TableCell>
                    <Row alignmentHorizontal="left" spacing={"none"}>
                        <Input width={"115px"}  type="text" size="small" value={typeFilter} onChange={setTypeFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.LAST_PICK) ?
                <TableCell>
                    <Row alignmentHorizontal="left" spacing={"none"}>
                        <Input width={"115px"}  type="text" size="small" value={lastPickCompletionTimeFilter} onChange={setLastPickCompletionTimeFilter}/>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_TYPE) && (
                    <TableCell/>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_STATUS) && (
                    <TableCell/>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_ASSOCIATE) && (
                    <TableCell/>
                )}
            <TableCell/>
        </TableRow>
    }

    const renderRouteRow = (route) => {
        return [<TableRow key={`Route_${route.routeId}`}>
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STAGE_BY_TIME) ?
                <TableCell>
                    <Tooltip position={"right"} title={convertEpochToLocaleFormat(route.criticalPullTime)}>
                        <Text alignment={"right"}>{route.routeSLA}</Text>
                    </Tooltip>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.PICK_BY_TIME) ?
                <TableCell alignmentHorizontal="right">{route.routeSLA}</TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) && configViewModel.stationConfig.parallelPickEnabled ?
                <TableCell alignmentHorizontal="right">
                    <Link data-expand-link={route.routeId} onClick={() => expandRoute(route.routeId)} type={"secondary"}
                          size={"small"}>
                        {isRouteExpanded(route.routeId) ? <Icon tokens={chevronDownSmallTokens}/> :
                            <Icon tokens={chevronLeftSmallTokens}/>}
                    </Link>
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) ?
                <TableCell>
                    <Row>
                        <Link onClick={() => {
                            onRouteClick(route)
                        }} type="secondary">{route.routeCode}</Link>
                    </Row>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.STATION_PAIRS) ?
                <TableCell>{route.stationPairs}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.STATION) ?
                <TableCell>{route.stationCode}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.PRIORITY) ?
                <TableCell alignmentHorizontal="right">{route.priority ? route.priority : "-"}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.AISLES) ?
                <TableCell>{route.aisles.length ? route.aisles.join(", ") : "-"}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.ASSIGNED_WORK_GROUP) && (
                    <TableCell>
                        {route.assignedWorkGroups && route.assignedWorkGroups.length > 0 ? (
                            route.assignedWorkGroups
                                .filter(group => group && group.trim().length > 0)
                                .map((group, index) => (
                                <React.Fragment key={index}>
                                    <span style={{
                                        padding: '4px 12px',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '5px',
                                        fontSize: '14px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        marginRight: '8px'
                                    }}>
                                        {group}
                                    </span>
                                </React.Fragment>
                            ))
                        ) : "-"}
                    </TableCell>
                )}

            {configViewModel.capability === CORE_AMZL &&
                configViewModel.stationConfig.multiStationPicklistViewEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.NEXT_WORK_GROUPS) && (
                    <TableCell>
                        {route.nextWorkGroups && route.nextWorkGroups.length > 0
                            ? route.nextWorkGroups.join(", ")
                            : "-"
                        }
                    </TableCell>
                )}

            {viewModelF.selectedPickColumns.includes(PickColumns.STATUS) ?
                <TableCell>
                    <Tooltip position="top" title={route.status}>
                        <Tag type={getPickStatusColor(route.status)}>{route.status.length > 13 ?
                            route.status.substr(0, 10) + "..." : route.status}</Tag>
                    </Tooltip>
                </TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.BAGS) ?
                <TableCell alignmentHorizontal="right">{route.bagCount == null ? "-" : route.bagCount}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.OVS) ?
                <TableCell alignmentHorizontal="right">{route.ovCount == null ? "-" : route.ovCount}</TableCell>
                : null
            }
            {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.SPR) ?
                <TableCell alignmentHorizontal="right">
                    <div className={getRouteAtRiskClassName(route)}>
                        {route.routeSPR == null ? "-" : route.routeSPR}
                    </div>
                </TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.CARTS) ?
                <TableCell
                    alignmentHorizontal="right">{route.cartAssigned === 0 ? "-" : route.cartAssigned}</TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.SPR) ?
                <TableCell alignmentHorizontal="right">{route.routeSPR == null ? "-" : route.routeSPR}</TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.PROGRESS) ?
                <TableCell alignmentHorizontal="right">{route.pickProgress}</TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.ASSOCIATE) ?
                <TableCell>
                    {renderAssociateCol(route)}
                </TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.DURATION) ?
                <TableCell alignmentHorizontal="right">{route.pickDuration}</TableCell>
                : null
            }
            {viewModelF.selectedPickColumns.includes(PickColumns.TYPE) ?
                <TableCell>{route.routeType}</TableCell>
                : null
            }
            {configViewModel.capability === AR_V2 && viewModelF.selectedPickColumns.includes(PickColumns.LAST_PICK) ?
                <TableCell alignmentHorizontal="right">{route.lastPickCompletionTime}</TableCell>
                : null
            }
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_TYPE) && (
                    <TableCell/>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_STATUS) && (
                    <TableCell/>
                )}
            {configViewModel.stationConfig.pickAssistEnabled &&
                viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_ASSOCIATE) && (
                    <TableCell/>
                )}
            {isAMZLorARv2(configViewModel.capability) ?
                <TableCell>
                    <RouteContextMenu route={route} routeList={viewModel.routeList}/>
                </TableCell>
                : null
            }
        </TableRow>
        ]
            .concat(isRouteExpanded(route.routeId) && configViewModel.stationConfig.parallelPickEnabled ?
                renderPicklistRows(route) : []);
    }

    const renderPicklistRows = (route) => {
        return route.picklists.map(picklist => {
            return <TableRow key={`Picklist_${picklist.picklistId}`} backgroundColor={"secondary"}>
                {viewModelF.selectedPickColumns.includes(PickColumns.STAGE_BY_TIME) ?
                    <TableCell/>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) ?
                    <TableCell/>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.ROUTE_ID) ?
                    <TableCell>
                        <Link onClick={() => {
                            onPicklistClicked(route, picklist.picklistId)
                        }} type="secondary">

                            {configViewModel.capability === CORE_AMZL && configViewModel.stationConfig.multiStationPicklistViewEnabled ? (
                                <Tooltip position="top" title={picklist.picklistType}>
                                    <span style={{ fontWeight: picklist.picklistType === PICK_TYPE.MULTI_STATION_PICKLIST ? 'bold' : 'normal' }}>
                                        {picklist.picklistCode}
                                    </span>
                                </Tooltip>
                            ) : (
                                <span>{picklist.picklistCode}</span>
                            )}
                        </Link>
                    </TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.STATION) ?
                    <TableCell>{picklist.stationCode}</TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.PRIORITY) ?
                    <TableCell/>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.AISLES) ?
                    <TableCell>{picklist.aisles.length ? picklist.aisles.join(", ") : "-"}</TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.ASSIGNED_WORK_GROUP) ?
                    <TableCell>
                        {picklist.assignedWorkGroupLabel ? (
                            <span style={{
                                padding: '4px 12px',
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                borderRadius: '5px',
                                fontSize: '14px',
                                alignItems: 'center',
                                display: 'inline-block'
                            }}>
                                {picklist.assignedWorkGroupLabel}
                            </span>
                        ) : "-"}
                    </TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.NEXT_WORK_GROUPS) ?
                    <TableCell>
                        {picklist.nextWorkGroups && picklist.nextWorkGroups.length > 0 ? (
                            picklist.nextWorkGroups.map((group, index) => (
                                <React.Fragment key={index}>
                                    <span style={{
                                        padding: '4px 12px',
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '5px',
                                        fontSize: '14px',
                                        display: 'inline-flex',
                                        alignItems: 'center'
                                    }}>
                                        {group}
                                    </span>
                                    {index < picklist.nextWorkGroups.length - 1 && (
                                        <span style={{
                                            color: '#666',
                                            marginRight: '8px'  // spacing between arrow and next group
                                        }}>→</span>
                                    )}
                                </React.Fragment>
                            ))
                        ) : "-"}
                    </TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.STATUS) ?
                    <TableCell>
                        <Tooltip position="top" title={picklist.status}>
                            <Tag type={getPickStatusColor(picklist.status)}>{picklist.status.length > 13 ?
                                picklist.status.substr(0, 10) + "..." : picklist.status}</Tag>
                        </Tooltip>
                    </TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.BAGS) ?
                    <TableCell alignmentHorizontal="right">{picklist.bagCount == null ? "-" : picklist.bagCount}</TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.OVS) ?
                    <TableCell alignmentHorizontal="right">{picklist.ovCount == null ? "-" : picklist.ovCount}</TableCell>
                    : null
                }
                {configViewModel.capability === CORE_AMZL && viewModelF.selectedPickColumns.includes(PickColumns.SPR) ?
                    <TableCell/>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.PROGRESS) ?
                    <TableCell alignmentHorizontal="right">{picklist.pickProgress}</TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.ASSOCIATE) ?
                    <TableCell>
                        {picklist.picker !== '-' ?
                            <Link onClick={() => {
                                onAssociateClick(picklist.picker)
                            }} type="secondary">{picklist.picker}</Link>
                            : "-"}
                    </TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.DURATION) ?
                    <TableCell alignmentHorizontal="right">{picklist.pickDuration}</TableCell>
                    : null
                }
                {viewModelF.selectedPickColumns.includes(PickColumns.TYPE) ?
                    <TableCell/>
                    : null
                }
                {configViewModel.stationConfig.pickAssistEnabled &&
                    viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_TYPE) && (
                        <TableCell>
                            {picklist.pickAssistSupportType ?
                                <Tag type={getPickAssistSupportTypeColor(picklist.pickAssistSupportType)}>
                                    <FormattedMessage {...getPickAssistSupportTypeLabel(picklist.pickAssistSupportType)}/>
                                </Tag>
                                : "-"}
                        </TableCell>
                    )}
                {configViewModel.stationConfig.pickAssistEnabled &&
                    viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_STATUS) && (
                        <TableCell>
                            {picklist.pickAssistStatus ?
                                <Tag type={getPickAssistStatusColor(picklist.pickAssistStatus)}>
                                    <FormattedMessage {...getPickAssistStatusLabel(picklist.pickAssistStatus)}/>
                                </Tag>
                                : "-"}
                        </TableCell>
                    )}
                {configViewModel.stationConfig.pickAssistEnabled &&
                    viewModelF.selectedPickColumns.includes(PickColumns.PICK_ASSIST_ASSOCIATE) && (
                        <TableCell>{picklist.pickAssistAssignee || "-"}</TableCell>
                    )}
                {configViewModel.capability === CORE_AMZL ?
                    <TableCell>
                        <PicklistContextMenu picklist={picklist}/>
                    </TableCell>
                    : null
                }
            </TableRow>;
        });
    }

    const renderPickTable = () => {
        return (
            <TableScrollWrapper>
                <Table
                    showDividers={isAMZLorARv2(configViewModel.capability)}
                    showStripes={configViewModel.capability === CORE_AMZL && !configViewModel.stationConfig.parallelPickEnabled}
                    headerRows={1}
                    spacing={"medium"}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    headerColumns={1}
                    stickyHeaderColumn={true}
                    fixHeaderRows={true}
                    stickyHeaderRow={true}
                >
                    {renderHeaderRow()}
                    {renderInlineFiltersRow()}
                    {routeList.map((route, key) => (key >= startPage && key <= endPage) ? renderRouteRow(route) : null)}
                </Table>
            </TableScrollWrapper>
        )
    }

    return (
        <div>
            <Row width="100%" maxWidth="100%" spacingInset="medium none medium none">
                {renderPickTable()}
            </Row>
            {routeList.length > 0 ?
                <Row width="100%" alignmentHorizontal="center" spacingInset="medium none medium none">
                    <Pagination
                        numberOfPages={pageSize}
                        onChange={setCurrentPage}
                        currentPage={currentPage}/>
                </Row> : null}
        </div>
    )
}

export default RoutePickStatus


// WEBPACK FOOTER //
// ./src/components/routePickStatus/RoutePickStatus.js